import MachproHTMLElement from './common/machproHtmlElement';
import {
  convertToInlineStyle, getReactProps, getSplitArr,
  shouldWebpFormat, hasVenusFormat,
} from '@m2h/common/utils/styleUtils';

const defaultClassName = 'machweb-image';
const innerImageClassName = 'machweb-image-inner';

class ImageComponent extends MachproHTMLElement {
  lastObjjectPosition = '';
  objectPositionSize: { height: string | null; width: string | null; } | null = null;
  resizeObserver: undefined | ResizeObserver;
  // private isProcessingStyle = false; // 防重入标志
  // private lastStyleValue = ''; // 记录上次的 style 值

  constructor() {
    super();
  }

  setImageAttribute(name, value) {
    if (name === 'src') {
      value = this.getRealSrcPath(value);
    }
    this.firstElementChild?.setAttribute(name, value);
  }

  // image 设置了 autoRtl 属性时的 RTL 镜像逻辑已迁移至
  // src/assets/styles/index.h5.css，通过 .machweb-image-inner 实现，
  // 避免覆盖外层 machweb-image 的业务 transform。
  // _getRTLStyleObj() {
  //   const htmlElement = document.documentElement;
  //   const dirValue = htmlElement.getAttribute('dir');
  //   const isAutoRtl = this.getAttribute('autortl');
  //   if (isAutoRtl && isAutoRtl === 'true' && dirValue === 'rtl') {
  //     // console.log('rtl----------');
  //     return { transform: 'scaleX(-1)' };
  //   }
  //   return {};
  // }

  _getDefaultStyleObj() {
    return {
      display: this.style.display || 'inline-flex',
      alignItems: this.style.alignItems || 'center',
      justifyContent: this.style.justifyContent || 'center',
      overflow: 'hidden',
    };
  }

  // 所有更新this.className的统一调用此处，更新class时，保持默认样式
  _updataClassName(newValue = this.className) {
    if (!this.className && !newValue) {
      this.className = defaultClassName;
      return;
    }

    if (this.className === newValue) return;

    const classArr = getSplitArr(newValue); // 拆为数组来判断，避免有包含相同字符串的情况
    const hasDefault = classArr.includes(defaultClassName);

    if (!hasDefault) {
      classArr.push(defaultClassName); // defaultClassName放最前
    }

    this.className = classArr.join(' ');
  }

  // 所有更新tthis.style.cssText的统一调用此处,新增方法：应用 style 属性
  _updateStyle(styleString, otherProcess?) {
    if (this.isProcessingStyle || this.lastStyleValue === styleString) return; // 防止重入
    try {
      this.isProcessingStyle = true;
      this.lastStyleValue = styleString; // 记录初始值
      this.style.cssText = styleString;
      otherProcess && otherProcess(styleString);
    } finally {
      this.isProcessingStyle = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // 先初始化外层元素（但暂不应用初始样式）
    this._updataClassName();
    this.initImageChild();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this; // timeout里使用this有点问题可能回到window
    setTimeout(() => {
      const reactProps = getReactProps(self) || {};
      if (reactProps.onLayout) {
        self.handleDomChange([reactProps.onLayout]);
      }
      // 获取babel转化后的初始style
      const mstyleStr = `${this.getAttribute('mstyle') || ''}`;
      // TODO 覆盖顺序问题深入验证
      const defaultStyleObj = {
        ...self._getDefaultStyleObj(),
        // RTL 镜像已通过 src/assets/styles/index.h5.css 的内层图片规则实现。
        // ...self._getRTLStyleObj(),
        ...reactProps.style, // react某些动态样式转换，在初始化时还没有值, setTimeout后获取
      };
      this._updateStyle(`${mstyleStr}${this.style.cssText}${convertToInlineStyle(defaultStyleObj)}`);
    }, 0);
  }

  disconnectedCallback() {
    this.resizeObserver && this.resizeObserver.unobserve(this);
    this.resizeObserver = undefined;
  }

  // 执行时机早于connectedCallback
  // 某些浏览器中，直接修改 style 对象也会触发 attributeChangedCallback
  attributeChangedCallback(name, oldValue, newValue) {
    // 值无变化不执行任何动作
    if (oldValue === newValue) return;

    if (name === 'mode' || name === 'resizemode') {
      return this.handleModeChange(newValue);
    }

    // className属性打包时处理为class，运行时需要在属性变化后同步到webcomponent
    if (name === 'className' || name === 'classname' || name === 'class') {
      // this.className = newValue;
      this._updataClassName(newValue);
      return;
    }

    if (name === 'style') {
      this._updateStyle(newValue);
      return;
    }

    if (name === 'src' && !this.firstElementChild) {
      return this.initImageChild();
    }

    this.setImageAttribute(name, newValue);
  }

  handleDomChange(callbacks: Function[] = []) {
    const handleCycleCb = () => {
      const detail = this.measureInWindow();
      callbacks.forEach((fn) => {
        fn({
          detail,
        });
      });
    };
    const resizeObserver = new ResizeObserver(handleCycleCb);
    // 开始观察传入的元素
    resizeObserver.observe(this);
    this.resizeObserver = resizeObserver;
    handleCycleCb();
  }

  // initOuterClass() {
  //   let wrapClassName = '';
  //   if (!this.className) {
  //     wrapClassName = defaultClassName;
  //   } else {
  //     wrapClassName = `${this.className} ${defaultClassName}`;
  //   }
  //   this.className = wrapClassName;
  // }

  initImageChild() {
    if (this.firstElementChild) return;
    const srcPath = this.getAttribute('src') || '';
    // 创建 img 标签
    const imgElement = document.createElement('img');
    imgElement.src = this.getRealSrcPath(srcPath);
    imgElement.className = innerImageClassName;
    imgElement.style.width = '100%';
    // 高度一般不自动填满，已宽度为基准自动比例伸缩
    imgElement.style.height = '100%';
    // imgElement.style.objectFit = 'contain';
    this.handleModeChange(
      // @ts-ignore
      this.getAttribute('mode') || this.getAttribute('resizemode') || this.style.mode || this.style.resizeMode,
      imgElement
    );
    this.handleLazyLoadChange(this.getAttribute('lazyLoad'), imgElement);
    imgElement.onload = this.handleLoadSuccess.bind(this, imgElement);
    imgElement.onerror = this.handleLoadError.bind(this, imgElement);
    this.appendChild(imgElement);
  }

  getRealSrcPath(src: string) {
    if (!src) {
      return src;
    }
    let resSrc = src;

    if (src.startsWith('http://')) {
      // console.log('aaaaaa', src);
      resSrc = src.replace('http://', 'https://');
    }

    // 处理 assets:// 协议
    if (src.indexOf('assets://') !== -1) {
      resSrc = src.replace('assets://', `${process_env_PUBLIC_PATH}assets/`);
    }

    // 处理相对路径形式的 assets 目录引用
    // 匹配 ../../../assets/ 或 ../../../../../assets/ 等形式
    const relativeAssetsRegex = /^(\.\.\/)+assets\//;
    if (relativeAssetsRegex.test(src)) {
      resSrc = src.replace(relativeAssetsRegex, `${process_env_PUBLIC_PATH}assets/`);
    }

    // venusCDN图片.jpg | .png才处理 && 本身没带Venus处理逻辑
    if (shouldWebpFormat(resSrc) && !hasVenusFormat(resSrc)) {
      // venus图片，添加@format=webp
      // resSrc = `${resSrc}@format=webp&lossless=1@limit_2000k`; // 不太支持多个同时
      // resSrc = `${resSrc}@format=webp&lossless=1`; // 压缩比例不大，优化效果有限
      resSrc = `${resSrc}@format=webp@limit_2000k`; // 此模式效果最好
      // resSrc = `${resSrc}@format=webp`; // 压缩比例最大，但本身是小图片的，反而变大或失真
      // resSrc = `${resSrc}@limit_200k`; // 大图片压缩效果有限
    }
    return resSrc;
  }

  handleLoadSuccess(imgElement: HTMLImageElement) {
    this.changeObjectPositionSize(imgElement);
  }

  changeObjectPositionSize(imgElement: HTMLImageElement) {
    const imgWidth = imgElement.naturalWidth;
    const imgHeight = imgElement.naturalHeight;
    const outerWidth = this.offsetWidth;
    const outerHeight = this.offsetHeight;
    if (!imgWidth || !imgHeight || !outerWidth || !outerHeight) return;
    const imgAspectRatio = imgWidth / imgHeight;
    const outerAspectRatio = outerWidth / outerHeight;
    const widthStyle = imgAspectRatio > outerAspectRatio ? 'auto' : '100%';
    const heightStyle = imgAspectRatio > outerAspectRatio ? '100%' : 'auto';
    if (widthStyle === this.objectPositionSize?.width && heightStyle === this.objectPositionSize?.height) return;
    this.objectPositionSize = {
      width: widthStyle,
      height: heightStyle,
    };
    if (this.lastObjjectPosition) {
      imgElement.style.width = widthStyle;
      imgElement.style.height = heightStyle;
    }
  }

  handleLoadError(imgElement: HTMLImageElement) {
    const placeholder = this.getRealSrcPath(this.getAttribute('placeholder') || '');
    const curUrl = imgElement?.getAttribute('url');
    if (!placeholder || placeholder === curUrl) {
      while (this.firstChild) {
        this.removeChild(this.firstChild);
      }
    } else {
      this.setImageAttribute('src', placeholder);
    }
  }

  // eslint-disable-next-line default-param-last
  handleModeChange(mode: string | null = 'aspectFit', imgElement?: HTMLImageElement) {
    if (this.firstElementChild) imgElement = this.firstElementChild as HTMLImageElement;
    if (!imgElement || !mode) return;
    let objectFit = 'contain';
    let objectPosition = '';
    let clearWidth = false;
    let clearHeight = false;
    if (mode === 'scaleToFill') {
      objectFit = 'fill';
    }
    if (mode === 'aspectFit') {
      objectFit = 'contain';
    }
    if (mode === 'aspectFill') {
      objectFit = 'cover';
    }
    if (mode === 'widthFix') {
      clearHeight = true;
    }
    if (mode === 'heightFix') {
      clearWidth = true;
    }
    if (
      mode
      && ['top', 'bottom', 'center', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'].includes(
        mode
      )
    ) {
      objectPosition = mode;
      clearHeight = false;
      clearWidth = false;
    }
    if (objectFit) {
      imgElement.style.objectFit = objectFit;
      imgElement.style.objectPosition = '';
    }
    if (objectPosition) {
      imgElement.style.objectPosition = objectPosition;
      imgElement.style.objectFit = 'contain';
      this.lastObjjectPosition = objectPosition;
      const { width, height } = this.objectPositionSize || {};
      width && (imgElement.style.width = width);
      height && (imgElement.style.height = height);
    }
    if (clearWidth) {
      imgElement.style.width = 'auto';
      imgElement.style.objectFit = '';
      imgElement.style.objectPosition = '';
    } else {
      !imgElement.style.width && (imgElement.style.width = '');
    }
    if (clearHeight) {
      imgElement.style.height = 'auto';
      imgElement.style.objectFit = '';
      imgElement.style.objectPosition = '';
    } else {
      !imgElement.style.height && (imgElement.style.height = '');
    }
  }

  handleLazyLoadChange(isLazyLoad: string | null, imgElement?: HTMLImageElement) {
    if (this.firstElementChild) imgElement = this.firstElementChild as HTMLImageElement;
    if (!imgElement) return;
    if (isLazyLoad === 'true') {
      imgElement.setAttribute('loading', 'lazy');
    } else {
      imgElement.setAttribute('loading', 'eager');
    }
  }

  static get observedAttributes() {
    return ['src', 'mode', 'resizemode', 'lazyLoad', 'className', 'classname', 'style', 'autortl'];
  }
}
export default ImageComponent;
